import * as Yup from "yup";
import { dropdownRequiredErrorMessage, inputFormatErrorMessage } from "../../../../../helper/YupValidationErrors";
import { validPositiveBigDecimal, validPositiveInteger } from "../../../../../helper/YupValidationHelpers";
import * as FieldNames from "../fieldNames";
import ProductReferenceResponse, {
  ProductReferenceResponseWithType,
} from "../../../../../api/models/ProductReferenceResponse";
import TestRequestPurpose from "../../../../../api/models/test-request/TestRequestPurpose";

export default function getValidationSchemaInternal(testRequestStatus: string, purpose: TestRequestPurpose) {
  const testPurpose =
    testRequestStatus === "DRAFT" ? Yup.string().required(dropdownRequiredErrorMessage) : Yup.string().nullable();

  const testEffortInDays =
    testRequestStatus === "FOR_EVALUATION" && purpose !== "APPLICATION_PROCESS"
      ? Yup.string().required("Test effort in days is required")
      : Yup.string().nullable();

  const estimatedFinishingDate =
    testRequestStatus === "FOR_EVALUATION" && purpose !== "APPLICATION_PROCESS"
      ? Yup.date()
          .nullable()
          .required("Estimated finishing date is required")
          .typeError("Invalid date format. Please use a valid date.")
      : Yup.date().nullable();

  return Yup.object().shape(
    {
      [FieldNames.APPLICATION_FIELD]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.APPROVER]: Yup.object().nullable(),
      [FieldNames.BACKGROUND_INFORMATION]: Yup.string().matches(/\w/, inputFormatErrorMessage).nullable(),
      [FieldNames.BUSINESS_TYPE]: Yup.string().nullable(),
      [FieldNames.BUSINESS_UNIT]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.CONFIDENTIALITY]: Yup.string().nullable().required(dropdownRequiredErrorMessage),
      [FieldNames.CRM_NO]: validPositiveInteger().nullable(),
      [FieldNames.CSC_LAB]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.CURRENCY]: Yup.string().required(),
      [FieldNames.DEADLINE]: Yup.date().nullable(),
      [FieldNames.DEBITOR_NO]: validPositiveInteger().nullable().notRequired(),
      [FieldNames.DIRECT_CUSTOMER]: Yup.object()
        .shape({
          id: Yup.number().required(),
          name: Yup.string().required(),
        })
        .required(dropdownRequiredErrorMessage),
      [FieldNames.DIRECT_CUSTOMER_LOCATION]: Yup.object()
        .shape({
          id: Yup.number().required(),
          name: Yup.string().required(),
        })
        .required(dropdownRequiredErrorMessage),
      [FieldNames.DIRECT_CUSTOMER_CGR]: Yup.string().nullable(),
      [FieldNames.FINISHING_DATE]: estimatedFinishingDate,
      [FieldNames.TEST_EFFORT_IN_DAYS]: testEffortInDays,
      [FieldNames.TEST_PURPOSE]: testPurpose.required(),
      [FieldNames.INTERNAL_CONTACTS]: Yup.array().nullable(),
      [FieldNames.MARKET_SEGMENT]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.PNS]: validPositiveBigDecimal().required(),
      [FieldNames.PRODUCTS]: Yup.string(),
      [FieldNames.REPORT_LANGUAGE]:
        purpose === "APPLICATION_PROCESS"
          ? Yup.string().nullable()
          : Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.REPORT_USAGE]: Yup.string().nullable(),
      [FieldNames.SOLUTION]: Yup.string().matches(/\w/, inputFormatErrorMessage),
      [FieldNames.SPECIFICATION]: Yup.array().nullable(),
      [FieldNames.SUBSTRATES]: purpose === "APPLICATION_PROCESS" ? Yup.string().nullable() : Yup.string().required(),
      [FieldNames.TEST_LOCATION]: Yup.string().nullable(),
      [FieldNames.TEST_PROGRAM]: Yup.string().nullable(),
      [FieldNames.PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.COMPETITOR_PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
        {
          is: (
            competitorProductReferences: ProductReferenceResponseWithType[],
            prototypeProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (competitorProductReferences && competitorProductReferences.length) ||
              (prototypeProductReferences && prototypeProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
      [FieldNames.PROTOTYPE_PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
        {
          is: (
            productReferences: ProductReferenceResponse[],
            competitorProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (productReferences && productReferences.length) ||
              (competitorProductReferences && competitorProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
      [FieldNames.COMPETITOR_PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
        {
          is: (
            productReferences: ProductReferenceResponse[],
            prototypeProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (productReferences && productReferences.length) ||
              (prototypeProductReferences && prototypeProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
    },
    [
      [FieldNames.PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
      [FieldNames.PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
      [FieldNames.PROTOTYPE_PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
    ],
  );
}
